import { IconButton } from "client/Components";
import { BlobIdImageElementComposerViewModel } from "client/Api/Hub/common.composer";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { clearFocus } from "client/Misc/domUtils";
import { Guid } from "client_legacy/ndaw/guid";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { EmbedInputDialogData } from "../../HubMediaSelect/EmbedInputDialog";
import { InlineActionList } from "../Actions/InlineActionList";
import {
	duplicateElement,
	Element,
	ElementHandle
} from "../Elements/Element";
import { AddElementButton } from "./AddElementButton";
import styles from "./Column.module.less";
import { AddElementMenu, AddElementMenuHandle } from "./Menus/AddElementMenu";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { VideoInputDialogData } from "../../HubMediaSelect/VideoInputDialog";
import { HubItemColumnFormModel, HubItemElementFormModel } from "../../Management/common";
import { HubItemElementKind } from "client/Api/Hub/common";
import { ImageWithAttribution, HubImageSelectorKind } from "../../Management/useHubImageSelector";

export type ColumnProps = {
	hubItemKind: HubItemKind;
	layoutLocation: { row: number; column: number; };
	attachable: IValueInputAttachable<ObjectWithClientId<HubItemColumnFormModel>>;
	originalImages: Map<number, BlobIdImageElementComposerViewModel>;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	onMoveRight?(): void;
	onMoveLeft?(): void;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
};

export type ColumnHandle = {
	findAndFocus(clientId: Guid): void;
	scrollIntoView(): void;
};

export const Column = forwardRef(
	(props: ColumnProps, ref: Ref<ColumnHandle>): ReactElement => {
		const {
			hubItemKind,
			layoutLocation,
			attachable,
			originalImages,
			onOpenEmbedInputDialog,
			onOpenVideoInputDialog,
			onMoveRight,
			onMoveLeft,
			onSelectImage
		} = props;

		const [hasFocus, setHasFocus] = useState<boolean>(false);
		const [isAddElementMenuOpen, setIsAddElementMenuOpen] = useState<boolean>(false);
		const [elementToScrollToId, setElementToScrollToId] = useState<Guid | undefined>(undefined);

		const addElementMenuRef: RefObject<AddElementMenuHandle | null> = useRef<AddElementMenuHandle | null>(null);

		const hasElements: boolean = attachable.value
			.elements
			.length > 0;

		const shouldRenderActions: boolean = hasFocus && hasElements;

		const containerRef = useRef<HTMLDivElement>(null);
		const addElementButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
		const addElementActionButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
		const elementRefs: ReadonlyRefObject<Map<Guid, (ElementHandle | null)>> =
			useRef<Map<Guid, (ElementHandle | null)>>(new Map());

		const setElementRef = useCallback(
			(element: ElementHandle | null, id: Guid) => {
				elementRefs.current
					.set(id, element);
			},
			[]
		);

		const findAndFocus = useCallback(
			(clientId: Guid): void => {
				elementRefs.current
					.forEach((x) => x?.findAndFocus(clientId));
			},
			[]
		);

		const addElementHandler = useCallback(
			(
				element: HubItemElementFormModel,
				afterIndex?: number
			): void => {
				const clientId: Guid = getClientId(
					setClientId(element)
				);

				const elementsAttachable: IValueInputAttachable<readonly HubItemElementFormModel[]> = attachable.for("elements");
				const elementsCopy: HubItemElementFormModel[] = [
					...elementsAttachable.value
				];

				let elementIndex: number;
				if (isNotNil(afterIndex) && afterIndex < elementsCopy.length - 1) {
					elementIndex = afterIndex + 1;
					elementsCopy.splice(elementIndex, 0, element);
				} else {
					elementsCopy.push(element);
					elementIndex = elementsCopy.length - 1;
				}

				elementsAttachable
					.reducer
					.setValue(elementsCopy);

				elementsAttachable.validation
					.triggerInputFinished();

				clearFocus();

				// The setTimeout is used because when elementsAttachable.whenType is called the reducer isn't updated yet
				// and it's accessing the old value
				setTimeout(
					(): void => {
						elementsAttachable.forIndex(elementIndex)
							.whenType(
								x => x.kind === HubItemElementKind.Image,
								image => image.for("image")
									.validation
									.triggerInputFinished()
							);
						findAndFocus(clientId);
					},
					1
				);
			},
			[attachable, findAndFocus]
		);

		const openAddElementMenuHandler = useCallback(
			(): void => {
				setIsAddElementMenuOpen(true);

				// We wait for the menu to be visible before focusing the first element
				setTimeout(
					(): void => {
						addElementMenuRef.current
							?.focusFirstElement();
					},
					0
				);
			},
			[]
		);

		const clearColumnHandler = useCallback(
			(): void => {
				attachable.for("elements")
					.reducer
					.setValue([]);

				attachable.validation
					.triggerInputFinished();

				clearFocus();
			},
			[attachable]
		);

		const removeElementHandler = useCallback(
			(index: number): void => {
				attachable.for("elements")
					.reducer
					.removeAt(index);

				attachable.validation
					.triggerInputFinished();

				clearFocus();
			},
			[attachable]
		);

		const duplicateHandler = useCallback(
			async (index: number): Promise<void> => {
				const elementsCopy: ObjectWithClientId<HubItemElementFormModel>[] = [
					...attachable.for("elements")
						.value
				];

				const itemToDuplicate: ObjectWithClientId<HubItemElementFormModel> = attachable.for("elements")
					.reducer
					.forIndex(index)
					.value;

				const duplicatedElement: ObjectWithClientId<HubItemElementFormModel> = await duplicateElement(
					itemToDuplicate,
					originalImages,
					hubItemKind
				);

				elementsCopy.splice(
					index,
					0,
					duplicatedElement
				);

				attachable.for("elements")
					.reducer
					.setValue(elementsCopy);

				attachable.validation
					.triggerInputFinished();

				clearFocus();
			},
			[
				attachable,
				originalImages,
				hubItemKind
			]
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				const elementsCopy: ObjectWithClientId<HubItemElementFormModel>[] = [
					...attachable.for("elements")
						.value
				];
				const itemToMove: ObjectWithClientId<HubItemElementFormModel> = elementsCopy
					.splice(fromIndex, 1)[0];

				elementsCopy.splice(
					toIndex,
					0,
					itemToMove
				);

				attachable.for("elements")
					.reducer
					.setValue(
						elementsCopy
					);

				setElementToScrollToId(
					getClientId(itemToMove)
				);

				attachable.validation
					.triggerInputFinished();
			},
			[attachable]
		);

		useLayoutEffect(
			(): void => {
				if (isNotNil(elementToScrollToId)) {
					elementRefs.current
						.get(elementToScrollToId)
						?.scrollIntoView();

					setElementToScrollToId(undefined);
				}
			},
			[elementToScrollToId]
		);

		useImperativeHandle(
			ref,
			(): ColumnHandle => ({
				findAndFocus,
				scrollIntoView(): void {
					containerRef.current
						?.scrollIntoView();
				}
			})
		);

		const openEmbedInputDialogHandler = useCallback(
			async (openData: EmbedInputDialogData): Promise<TryResult<string>> => {
				clearFocus();
				return await onOpenEmbedInputDialog(openData);
			},
			[onOpenEmbedInputDialog]
		);

		const openVideoInputDialogHandler = useCallback(
			async (openData: VideoInputDialogData): Promise<TryResult<string>> => {
				clearFocus();
				return await onOpenVideoInputDialog(openData);
			},
			[onOpenVideoInputDialog]
		);

		const renderAddElementMenuFor = (target: HTMLElement): ReactElement | null => {
			return (
				<AddElementMenu
					ref={addElementMenuRef}
					target={target}
					onClose={() => setIsAddElementMenuOpen(false)}
					onNewElement={addElementHandler}
					onOpenEmbedInputDialog={openEmbedInputDialogHandler}
					onOpenVideoInputDialog={openVideoInputDialogHandler}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
					onSelectImage={onSelectImage}
				/>
			);
		};

		return (
			<div
				{...setTrackingId(attachable.reducer.path)}
				ref={containerRef}
				className={styles.root}
				onClick={(e) => {
					e.stopPropagation();
				}}
				onFocus={(e)=>{
					e.stopPropagation();
					setHasFocus(true);
				}}
				onBlur={
					() => {
						setHasFocus(false);
					}
				}
			>
				<div className={styles.actionsDropOverAnchor}>
					{shouldRenderActions && (
						<div className={styles.actionsDropOver}>
							<InlineActionList trackingId="column-actions">
								<IconButton
									ref={addElementActionButtonRef}
									kind="transparent-colored"
									icon="plus"
									onClick={openAddElementMenuHandler}
									trackingId="add-button"
								/>
								{isAddElementMenuOpen && isNotNil(addElementActionButtonRef.current)
									&& renderAddElementMenuFor(addElementActionButtonRef.current)
								}
								{onMoveLeft && (
									<IconButton
										kind="transparent-colored"
										icon="arrow-left"
										onClick={onMoveLeft}
										trackingId="move-left-button"
									/>
								)}
								{onMoveRight && (
									<IconButton
										kind="transparent-colored"
										icon="arrow-right"
										onClick={onMoveRight}
										trackingId="move-right-button"
									/>
								)}
								<IconButton
									kind="transparent-colored"
									icon="trash-can"
									onClick={clearColumnHandler}
									trackingId="delete-button"
								/>
							</InlineActionList>
						</div>
					)}
				</div>
				{!hasElements && (
					<AddElementButton
						ref={addElementButtonRef}
						isActive={isAddElementMenuOpen}
						onClick={openAddElementMenuHandler}
						trackingId="add-element-button"
					/>
				)}
				{isAddElementMenuOpen && isNotNil(addElementButtonRef.current)
					&& renderAddElementMenuFor(addElementButtonRef.current)
				}

				<div className={styles.elementList}>
					{attachable.for("elements")
						.map((elementAttachable, index) => {
							const clientId: Guid = getClientId(elementAttachable.value);

							return (
								<Element
									ref={(e: ElementHandle) => setElementRef(e, clientId)}
									key={clientId}
									layoutLocation={{ ...layoutLocation, element: index }}
									hubItemKind={hubItemKind}
									attachable={elementAttachable}
									onAddNewElementToColumn={
										(element) =>
											addElementHandler(
												element,
												index
											)
									}
									onDuplicate={() => duplicateHandler(index)}
									onDelete={() => removeElementHandler(index)}
									originalImages={originalImages}
									onOpenEmbedInputDialog={openEmbedInputDialogHandler}
									onOpenVideoInputDialog={openVideoInputDialogHandler}
									onMoveDown={index < attachable.for("elements").value.length - 1
										? () => itemMoveHandler(index, index + 1)
										: undefined
									}
									onMoveUp={index > 0
										? () => itemMoveHandler(index, index - 1)
										: undefined
									}
									onSelectImage={onSelectImage}
								/>
							);
						})
					}
				</div>
			</div>
		);
	}
);
Column.displayName="Column";